import { useRef, useState } from "react";
import { enquiryFormEndpoint } from "../constants/business";
import { FORM_STRINGS } from "../constants/strings";
import {
  EMAIL_PATTERN,
  MESSAGE_MAX,
  NAME_MAX,
  NAME_PATTERN,
  PHONE_PATTERN,
} from "../constants/validation";

const initialEnquiryState = {
  name: "",
  phone: "",
  email: "",
  enquiryType: FORM_STRINGS.enquiryTypes[0],
  message: "",
};

function validateEnquiry(enquiry) {
  if (!enquiry.name.trim() || !NAME_PATTERN.test(enquiry.name)) {
    return FORM_STRINGS.errors.name;
  }
  if (!PHONE_PATTERN.test(enquiry.phone)) {
    return FORM_STRINGS.errors.phone;
  }
  if (enquiry.email.trim() && !EMAIL_PATTERN.test(enquiry.email.trim())) {
    return FORM_STRINGS.errors.email;
  }
  if (enquiry.message.length > MESSAGE_MAX) {
    return FORM_STRINGS.errors.message(MESSAGE_MAX);
  }
  return "";
}

function EnquiryForm() {
  const [enquiry, setEnquiry] = useState(initialEnquiryState);
  const [formStatus, setFormStatus] = useState("idle");
  const [formError, setFormError] = useState("");
  const messageRef = useRef(null);

  function handleEnquiryChange(event) {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "name") {
      nextValue = value.replace(/[^A-Za-z ]/g, "").slice(0, NAME_MAX);
    } else if (name === "phone") {
      nextValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "message") {
      nextValue = value.slice(0, MESSAGE_MAX);
    }

    setEnquiry((prev) => ({ ...prev, [name]: nextValue }));
    setFormStatus("idle");
    setFormError("");

    if (name === "message" && messageRef.current) {
      messageRef.current.style.height = "auto";
      messageRef.current.style.height = `${messageRef.current.scrollHeight}px`;
    }
  }

  async function handleEnquirySubmit(event) {
    event.preventDefault();

    const validationMessage = validateEnquiry(enquiry);
    if (validationMessage) {
      setFormStatus("invalid");
      setFormError(validationMessage);
      return;
    }

    if (!enquiryFormEndpoint) {
      setFormStatus("not-configured");
      return;
    }

    setFormStatus("submitting");

    try {
      const body = new FormData();
      Object.entries(enquiry).forEach(([key, value]) =>
        body.append(key, value)
      );

      await fetch(enquiryFormEndpoint, {
        method: "POST",
        mode: "no-cors",
        body,
      });

      setFormStatus("sent");
      setEnquiry(initialEnquiryState);
    } catch (error) {
      setFormStatus("error");
    }
  }

  return (
    <form className="enquiry-form" onSubmit={handleEnquirySubmit} noValidate>
      <label htmlFor="enquiry-name">{FORM_STRINGS.nameLabel}</label>
      <input
        id="enquiry-name"
        name="name"
        type="text"
        autoComplete="name"
        inputMode="text"
        pattern="[A-Za-z ]+"
        required
        value={enquiry.name}
        onChange={handleEnquiryChange}
      />

      <label htmlFor="enquiry-phone">{FORM_STRINGS.phoneLabel}</label>
      <input
        id="enquiry-phone"
        name="phone"
        type="tel"
        autoComplete="tel"
        inputMode="numeric"
        pattern="\d{10}"
        required
        value={enquiry.phone}
        onChange={handleEnquiryChange}
      />

      <label htmlFor="enquiry-email">{FORM_STRINGS.emailLabel}</label>
      <input
        id="enquiry-email"
        name="email"
        type="email"
        autoComplete="email"
        value={enquiry.email}
        onChange={handleEnquiryChange}
      />

      <label htmlFor="enquiry-type">{FORM_STRINGS.enquiryTypeLabel}</label>
      <select
        id="enquiry-type"
        name="enquiryType"
        value={enquiry.enquiryType}
        onChange={handleEnquiryChange}
      >
        {FORM_STRINGS.enquiryTypes.map((type) => (
          <option value={type} key={type}>
            {type}
          </option>
        ))}
      </select>

      <label htmlFor="enquiry-message">{FORM_STRINGS.messageLabel}</label>
      <div className="textarea-wrap">
        <textarea
          id="enquiry-message"
          name="message"
          rows="4"
          maxLength={MESSAGE_MAX}
          required
          value={enquiry.message}
          onChange={handleEnquiryChange}
          ref={messageRef}
        />
        <span className="textarea-counter">
          {enquiry.message.length}/{MESSAGE_MAX}
        </span>
      </div>

      <button
        className="button button-primary"
        type="submit"
        disabled={formStatus === "submitting"}
      >
        {formStatus === "submitting"
          ? FORM_STRINGS.submittingLabel
          : FORM_STRINGS.submitLabel}
      </button>

      {formStatus === "invalid" && (
        <p className="form-status form-status-error">{formError}</p>
      )}
      {formStatus === "sent" && (
        <p className="form-status form-status-success">
          {FORM_STRINGS.success}
        </p>
      )}
      {formStatus === "error" && (
        <p className="form-status form-status-error">
          {FORM_STRINGS.genericError}
        </p>
      )}
      {formStatus === "not-configured" && (
        <p className="form-status form-status-error">
          {FORM_STRINGS.notConfigured}
        </p>
      )}
    </form>
  );
}

export default EnquiryForm;
